import type { UserRole } from './AuthContext';

/**
 * isFullAdminRole reports whether a role has unrestricted admin authority —
 * true for both 'super_admin' and 'admin' (the middle tier has identical
 * permissions to Super Admin except it cannot promote). Mirrors the backend
 * Role.IsFullAdmin() (internal/users/domain/user.go). Use this instead of
 * `role === 'admin'` for any "is this an unrestricted admin" check — a bare
 * 'admin' comparison now excludes Super Admin, the opposite of intent.
 */
export function isFullAdminRole(role: UserRole | string | undefined): boolean {
  return role === 'super_admin' || role === 'admin';
}

/**
 * isSuperAdminRole reports whether a role is specifically Super Admin, not
 * just full-admin-tier — distinct from isFullAdminRole, which intentionally
 * treats 'admin' and 'super_admin' as equivalent. Needed for the small set of
 * checks where Super Admin has strictly more authority than a regular admin
 * (e.g. bypassing a locked thread to delete a message). Mirrors the backend
 * Role == RoleSuperAdmin check (internal/users/domain/user.go).
 */
export function isSuperAdminRole(role: UserRole | string | undefined): boolean {
  return role === 'super_admin';
}

/**
 * isBrandOwnerTier reports whether a role is the owner-tier of the brand lane
 * — true for both 'brand_owner' and 'brand_admin' (brand_admin has the same
 * campaign/settings authority as the owner, short of billing). Use this
 * instead of inlining `role === 'brand_owner' || role === 'brand_admin'` at
 * each call site — mirrors isFullAdminRole's role for the admin lane.
 */
export function isBrandOwnerTier(role: UserRole | string | null | undefined): boolean {
  return role === 'brand_owner' || role === 'brand_admin';
}

/**
 * adminRoleLabel maps an admin-team role to its UI display label.
 * Single source of truth for the "Super Admin" / "Admin" / "Staff" copy —
 * previously four divergent inline copies across the admin app, several of
 * which only handled two roles and would have silently mislabeled the new
 * Admin tier as Staff.
 */
export function adminRoleLabel(role: UserRole | string | undefined): string {
  switch (role) {
    case 'super_admin':
      return 'Super Admin';
    case 'admin':
      return 'Admin';
    default:
      return 'Staff';
  }
}
